import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";
import { normalizeZoneCharge } from "src/utils/pricing/zoneChargeEngine";

// Promociones horarias (price rules).
//
// API:
//   GET    price-rules              → [rule]            (gestión)
//   POST   price-rules              → rule
//   GET    price-rules/{id}         → rule
//   PUT    price-rules/{id}         → rule
//   DELETE price-rules/{id}
//   PATCH  price-rules/{id}/active  { isActive } → rule
//   GET    price-rules/active       → { rules, zoneCharges, nowLocal, timeZone, businessDayStartHour }
//
// Suplementos por zona (P3):
//   GET    zone-charges             → [charge]
//   POST   zone-charges             → charge
//   PUT    zone-charges/{id}        → charge
//   DELETE zone-charges/{id}
//
// El SERVIDOR decide el precio al comandar. El TPV solo previsualiza con
// `src/utils/pricing/pricingEngine.js` usando `active` (cache 60 s).
// Todo el parseo es defensivo: el API se construye en paralelo.

const initialState = {
  // Gestión (pantalla «Promociones horarias»).
  list: [],
  listLoaded: false,
  isLoading: false,
  // Reglas activas para la previsualización del TPV.
  // Gestión de suplementos por zona.
  zoneCharges: [],
  zoneChargesLoaded: false,
  active: {
    rules: [],
    zoneCharges: [],
    nowLocal: null,
    timeZone: null,
    businessDayStartHour: null,
    fetchedAtMs: null,
  },
};

const asArray = (v) => (Array.isArray(v) ? v : []);

/** Normaliza una regla del API (camelCase o PascalCase, nulos, etc.). */
export function normalizeRule(r) {
  if (!r || typeof r !== "object") return null;
  const pick = (a, b) => (r[a] !== undefined ? r[a] : r[b]);
  // El API devuelve `productIds`/`categoryIds`/`zoneIds` planos; el diseño
  // hablaba de `targets`/`zones`. Aceptamos los dos.
  const targets = [
    ...asArray(pick("targets", "Targets")).map((t) => ({
      productId: t?.productId ?? t?.ProductId ?? null,
      categoryId: t?.categoryId ?? t?.CategoryId ?? null,
    })),
    ...asArray(pick("productIds", "ProductIds")).map((id) => ({ productId: id, categoryId: null })),
    ...asArray(pick("categoryIds", "CategoryIds")).map((id) => ({ productId: null, categoryId: id })),
  ];
  const zones = [
    ...asArray(pick("zones", "Zones")),
    ...asArray(pick("zoneIds", "ZoneIds")),
  ].map(Number);
  const weekdays = pick("weekdays", "Weekdays");
  const channels = pick("channels", "Channels");
  return {
    id: pick("id", "Id"),
    name: pick("name", "Name") || "",
    isActive: pick("isActive", "IsActive") !== false,
    kind: String(pick("kind", "Kind") || "PERCENT_OFF").toUpperCase(),
    value: Number(pick("value", "Value")) || 0,
    scope: String(pick("scope", "Scope") || "ALL").toUpperCase(),
    targets,
    zones: Array.from(new Set(zones)),
    // Sin campo → todos los días (default del API); 0 → ninguno.
    weekdays: weekdays == null ? 127 : Number(weekdays) || 0,
    startTime: pick("startTime", "StartTime") ? String(pick("startTime", "StartTime")).slice(0, 5) : null,
    endTime: pick("endTime", "EndTime") ? String(pick("endTime", "EndTime")).slice(0, 5) : null,
    validFrom: pick("validFrom", "ValidFrom") ? String(pick("validFrom", "ValidFrom")).slice(0, 10) : null,
    validTo: pick("validTo", "ValidTo") ? String(pick("validTo", "ValidTo")).slice(0, 10) : null,
    channels: channels == null ? 1 : Number(channels) || 0,
    priority: Number(pick("priority", "Priority")) || 0,
  };
}

const slice = createSlice({
  name: "priceRules",
  initialState,
  reducers: {
    setLoading(state, action) {
      state.isLoading = !!action.payload;
    },
    setList(state, action) {
      state.list = asArray(action.payload).map(normalizeRule).filter(Boolean);
      state.listLoaded = true;
    },
    upsertRule(state, action) {
      const r = normalizeRule(action.payload);
      if (!r) return;
      const i = state.list.findIndex((x) => x.id === r.id);
      if (i >= 0) state.list[i] = r;
      else state.list.push(r);
    },
    removeRule(state, action) {
      state.list = state.list.filter((x) => x.id !== action.payload);
    },
    setActive(state, action) {
      const p = action.payload || {};
      // `enabled:false` del API (ajuste apagado en servidor) → sin reglas.
      const serverEnabled = (p.enabled ?? p.Enabled) !== false;
      state.active = {
        rules: serverEnabled
          ? asArray(p.rules ?? p.Rules).map(normalizeRule).filter(Boolean)
          : [],
        // Suplementos por zona (P3). `zoneChargesEnabled:false` del API →
        // sin suplementos.
        zoneCharges: (p.zoneChargesEnabled ?? p.ZoneChargesEnabled) !== false
          ? asArray(p.zoneCharges ?? p.ZoneCharges).map(normalizeZoneCharge).filter(Boolean)
          : [],
        // El API lo llama `localNow` (diseño: `nowLocal`).
        nowLocal: p.nowLocal ?? p.localNow ?? p.NowLocal ?? p.LocalNow ?? null,
        timeZone: p.timeZone ?? p.timeZoneId ?? p.TimeZone ?? p.TimeZoneId ?? null,
        businessDayStartHour: p.businessDayStartHour ?? p.BusinessDayStartHour ?? null,
        fetchedAtMs: p.fetchedAtMs ?? Date.now(),
      };
    },
    setZoneCharges(state, action) {
      state.zoneCharges = asArray(action.payload).map(normalizeZoneCharge).filter(Boolean);
      state.zoneChargesLoaded = true;
    },
    upsertZoneCharge(state, action) {
      const z = normalizeZoneCharge(action.payload);
      if (!z) return;
      if (!Array.isArray(state.zoneCharges)) state.zoneCharges = [];
      const i = state.zoneCharges.findIndex((x) => x.id === z.id);
      if (i >= 0) state.zoneCharges[i] = z;
      else state.zoneCharges.push(z);
    },
    removeZoneCharge(state, action) {
      state.zoneCharges = asArray(state.zoneCharges).filter((x) => x.id !== action.payload);
    },
    clearActive(state) {
      state.active = { ...initialState.active };
    },
  },
});

export default slice.reducer;
export const { setActive: setActivePriceRules, clearActive: clearActivePriceRules } = slice.actions;

// ── Reglas activas (TPV) ─────────────────────────────────────────────────

let inflight = null;

/**
 * Carga `price-rules/active`. Sin `force`, no repite si la última carga
 * tiene menos de `minAgeMs` (varias vistas del TPV montan el hook).
 */
export function fetchActivePriceRules({ force = false, minAgeMs = 15000 } = {}) {
  return async (dispatch, getState) => {
    const last = getState()?.priceRules?.active?.fetchedAtMs;
    if (!force && last && Date.now() - last < minAgeMs) return;
    if (inflight) return inflight;
    inflight = (async () => {
      try {
        const res = await axios.get("price-rules/active");
        const data = res?.data || {};
        dispatch(slice.actions.setActive({ ...data, fetchedAtMs: Date.now() }));
      } catch (e) {
        // Sin reglas: el TPV pinta precios de carta (el servidor manda).
        if (process.env.NODE_ENV !== "production") console.warn("price-rules/active", e);
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  };
}

// ── Gestión ──────────────────────────────────────────────────────────────

export function fetchPriceRules() {
  return async (dispatch) => {
    dispatch(slice.actions.setLoading(true));
    try {
      const res = await axios.get("price-rules");
      const data = res?.data;
      dispatch(slice.actions.setList(Array.isArray(data) ? data : data?.rules || []));
    } finally {
      dispatch(slice.actions.setLoading(false));
    }
  };
}

/** Payload del API a partir del formulario ya validado. */
export function toApiPayload(form) {
  const scope = form.scope || "ALL";
  const productIds = scope === "PRODUCT" ? asArray(form.productIds) : [];
  const categoryIds = scope === "CATEGORY" ? asArray(form.categoryIds) : [];
  const zoneIds = asArray(form.zones).map(Number);
  return {
    name: String(form.name || "").trim(),
    isActive: form.isActive !== false,
    kind: form.kind,
    value: Number(form.value),
    scope,
    // Formato del API (planos) + formato del diseño (targets/zones).
    productIds,
    categoryIds,
    zoneIds,
    targets: [
      ...productIds.map((id) => ({ productId: id })),
      ...categoryIds.map((id) => ({ categoryId: id })),
    ],
    zones: zoneIds,
    weekdays: Number(form.weekdays) || 0,
    startTime: form.allDay ? null : form.startTime || null,
    endTime: form.allDay ? null : form.endTime || null,
    validFrom: form.validFrom || null,
    validTo: form.validTo || null,
    channels: Number(form.channels) || 1,
    priority: Number(form.priority) || 0,
  };
}

export function savePriceRule(form) {
  return async (dispatch) => {
    const payload = toApiPayload(form);
    const res = form.id
      ? await axios.put(`price-rules/${form.id}`, { id: form.id, ...payload })
      : await axios.post("price-rules", payload);
    dispatch(slice.actions.upsertRule(res?.data && typeof res.data === "object" ? res.data : { id: form.id, ...payload }));
    dispatch(fetchActivePriceRules({ force: true }));
    return res?.data;
  };
}

export function setPriceRuleActive(id, isActive) {
  return async (dispatch, getState) => {
    const prev = getState()?.priceRules?.list?.find((r) => r.id === id);
    if (prev) dispatch(slice.actions.upsertRule({ ...prev, isActive }));
    try {
      const res = await axios.patch(`price-rules/${id}/active`, { isActive });
      if (res?.data && typeof res.data === "object" && (res.data.id ?? res.data.Id) != null) {
        dispatch(slice.actions.upsertRule(res.data));
      }
      dispatch(fetchActivePriceRules({ force: true }));
    } catch (e) {
      if (prev) dispatch(slice.actions.upsertRule(prev));
      throw e;
    }
  };
}

export function deletePriceRule(id) {
  return async (dispatch) => {
    await axios.delete(`price-rules/${id}`);
    dispatch(slice.actions.removeRule(id));
    dispatch(fetchActivePriceRules({ force: true }));
  };
}

// ── Suplementos por zona (gestión) ───────────────────────────────────────

export function fetchZoneCharges() {
  return async (dispatch) => {
    const res = await axios.get("zone-charges");
    const data = res?.data;
    dispatch(slice.actions.setZoneCharges(Array.isArray(data) ? data : data?.zoneCharges || data?.items || []));
  };
}

export function saveZoneCharge(payload) {
  return async (dispatch) => {
    const res = payload.id
      ? await axios.put(`zone-charges/${payload.id}`, payload)
      : await axios.post("zone-charges", payload);
    dispatch(slice.actions.upsertZoneCharge(
      res?.data && typeof res.data === "object" && (res.data.id ?? res.data.Id) != null ? res.data : payload,
    ));
    dispatch(fetchActivePriceRules({ force: true }));
    return res?.data;
  };
}

/** Activa/desactiva reutilizando PUT (el API no tiene PATCH propio). */
export function setZoneChargeActive(charge, isActive) {
  return async (dispatch) => {
    dispatch(slice.actions.upsertZoneCharge({ ...charge, isActive }));
    try {
      await dispatch(saveZoneCharge({ ...charge, isActive }));
    } catch (e) {
      dispatch(slice.actions.upsertZoneCharge(charge));
      throw e;
    }
  };
}

export function deleteZoneCharge(id) {
  return async (dispatch) => {
    await axios.delete(`zone-charges/${id}`);
    dispatch(slice.actions.removeZoneCharge(id));
    dispatch(fetchActivePriceRules({ force: true }));
  };
}
